import type { IsoDate } from '../api/contract';

const PARIS = 'Europe/Paris';

/** Date civile du jour à Paris, au format AAAA-MM-JJ. */
export function todayInParis(now: Date = new Date()): IsoDate {
  return new Intl.DateTimeFormat('en-CA', { timeZone: PARIS }).format(now);
}

/** Heure courante à Paris (0-23). */
export function hourInParis(now: Date = new Date()): number {
  return Number(new Intl.DateTimeFormat('en-GB', { timeZone: PARIS, hour: '2-digit', hourCycle: 'h23' }).format(now));
}

export function addDays(date: IsoDate, days: number): IsoDate {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}

export function dayOfYear(date: IsoDate): number {
  const d = Date.parse(`${date}T12:00:00Z`);
  const start = Date.parse(`${date.slice(0, 4)}-01-01T12:00:00Z`);
  return Math.round((d - start) / 86_400_000) + 1;
}

export function isIsoDate(value: string | null): value is IsoDate {
  return value != null && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value));
}

/** Jours passés et jours de prévision de la frise : un mois d'historique pour voir toute la séquence pluie → pousse. */
export const FRISE_PAST = 30;
export const FRISE_FORECAST = 6;

/** Les jours de la frise : 30 jours passés, aujourd'hui, 6 jours de prévision. */
export function friseDays(today: IsoDate): IsoDate[] {
  return Array.from({ length: FRISE_PAST + 1 + FRISE_FORECAST }, (_, i) => addDays(today, i - FRISE_PAST));
}

const fmt = (options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('fr-FR', { timeZone: 'UTC', ...options });
const longFmt = fmt({ weekday: 'long', day: 'numeric', month: 'long' });
const shortFmt = fmt({ weekday: 'short', day: 'numeric', month: 'short' });
const weekdayShortFmt = fmt({ weekday: 'short' });
const weekdayFmt = fmt({ weekday: 'long' });
const dayFmt = fmt({ day: 'numeric' });

const asDate = (date: IsoDate) => new Date(`${date}T12:00:00Z`);

/** « samedi 18 octobre » */
export const formatLong = (date: IsoDate) => longFmt.format(asDate(date));
/** « sam. 18 oct. » */
export const formatShort = (date: IsoDate) => shortFmt.format(asDate(date));
/** « sam. » */
export const formatWeekdayShort = (date: IsoDate) => weekdayShortFmt.format(asDate(date));
/** « samedi » */
export const formatWeekday = (date: IsoDate) => weekdayFmt.format(asDate(date));
/** « 18 » */
export const formatDayNumber = (date: IsoDate) => dayFmt.format(asDate(date));

/** « aujourd'hui », « hier », « demain », sinon le jour long. */
export function formatRelative(date: IsoDate, today: IsoDate): string {
  const diff = daysBetween(today, date);
  if (diff === 0) return "aujourd'hui";
  if (diff === -1) return 'hier';
  if (diff === 1) return 'demain';
  return formatLong(date);
}
